import axios from 'axios';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { DateFormat } from '../../../utils/DateFormat';
import { notresultsfound } from '../../../assets/images/icons';

export default function TransactionsList() {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const localhost = process.env.REACT_APP_LOCALHOST;
  const [transactions, setTransactions] = useState([]);
  const [filterTransactions, setFilterTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1 });

  // Fetch transactions
  const fetchTransactions = async (page = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(localhost + `admin/transactions-list?page=${page}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data.data;
      const pagination = res.data.pagination;
      setTransactions(data);
      setFilterTransactions(data);
      setPagination({ currentPage: pagination.current_page, totalPages: pagination.last_page });
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  // Paginate transactions
  const handlePagination = useCallback((page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      fetchTransactions(page);
      setPagination((prev) => ({ ...prev, currentPage: page }));
    }
  }, [pagination.totalPages]);

  // Search transactions by cardholder name
  const handleSearch = (e) => {
    e.preventDefault();
    const searchTerm = e.target.value.toLowerCase();
    const filtered = transactions.filter((transaction) =>
      transaction.cardholder_name.toLowerCase().includes(searchTerm)
    );
    setFilterTransactions(filtered);
  };

  useEffect(() => {
    fetchTransactions(pagination.currentPage);
  }, [pagination.currentPage]);

  // Table header
  const ths = ["Transaction ID", "date", "fullname", "payment-method", "amount", "status"];

  const MobileCard = ({ item }) => (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <div className="flex justify-between items-start mb-3">
        <div>
          <span className="text-xs text-gray-500 block mb-1">{t("order-id")}</span>
          <span className="font-semibold text-gray-900"># {item.num_transaction}</span>
        </div>
      </div>
      <dl className="space-y-3">
        <div>
          <dt className="text-xs text-gray-500 mb-1">{t("date")}</dt>
          <dd className="text-sm text-gray-900"><DateFormat dateString={item.payment_date} /></dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500 mb-1">{t("fullname")}</dt>
          {/* <dd className="text-sm text-gray-900">{item.cardholder_name}</dd> */}
          <dd className="text-sm text-gray-900">{item.full_name ? item.full_name : item.guest_name}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500 mb-1">{t("payment-method")}</dt>
          <dd className="text-sm text-gray-900">{t(item.payment_method)}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500 mb-1">{t("amount")}</dt>
          <dd className="text-sm text-gray-900">{item.transaction_amount} MAD</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500 mb-1">{t("status")}</dt>
          <dd className="text-sm text-gray-900"><StatusBox t={t} statusProp={item.payment_status} /></dd>
        </div>
      </dl>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">{t("all-transactions")}</h1>
      </div>

      {/* Search Section */}
      <div className="border border-gray-200 bg-white rounded-lg shadow-sm p-4">
        <form onSubmit={handleSearch} className="flex gap-2 max-w-md">
          <input className="flex-1 border border-gray-300 rounded-lg px-4 py-1.5 text-sm outline-none transition-all" placeholder={t("search-transactions")} type="text" value={search} onChange={(e) => setSearch(e.target.value)} />
          <button type="submit" className="px-3 py-1.5 bg-black hover:bg-black/80 text-white rounded-lg transition-colors duration-200 flex items-center gap-2">
            <MagnifyingGlassIcon className="h-5 w-5" />
            <span className="hidden sm:inline">{t("search")}</span>
          </button>
        </form>
      </div>

      {/* Content Section */}
      {loading ? (
        <div className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white min-h-[55vh] flex items-center justify-center">
          <div className="loading-spinner" />
        </div>
      ) : filterTransactions.length > 0 ? (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block bg-white shadow-sm rounded-lg">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {ths.map((item, index) => (
                    <th key={index} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t(item)}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filterTransactions.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 text-sm"># {item.num_transaction}</td>
                    <td className="px-6 py-4 text-sm text-gray-600"><DateFormat dateString={item.payment_date} /> </td>
                    {/* <td className="px-6 py-4 text-sm text-gray-600">{item.cardholder_name}</td> */}
                    <td className="px-6 py-4 text-sm text-gray-600">{item.full_name ? item.full_name : item.guest_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{t(item.payment_method)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{item.transaction_amount} MAD</td>
                    <td className="px-6 py-4 text-sm"><StatusBox t={t} statusProp={item.payment_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden grid gap-4">
            {filterTransactions.map((item, index) => (
              <MobileCard key={index} item={item} />
            ))}
          </div>

          <div className="flex items-center justify-center gap-4 my-8">
            <button onClick={() => handlePagination(pagination.currentPage - 1)} disabled={pagination.currentPage === 1}
              className="inline-flex items-center text-sm font-medium transition-colors rounded-sm disabled:opacity-30 disabled:cursor-not-allowed bg-white text-black/80 hover:bg-gray-50 hover:text-black focus:outline-none focus:ring-1 focus:ring-orange-500">
              <ChevronLeftIcon className="size-6" />
            </button>
            <div className="flex items-center text-sm font-medium text-black/80">
              <span className="px-3 py-1 rounded-lg bg-black/5">{pagination.currentPage}</span>
              <span className="mx-2">{t("of")}</span>
              <span className="px-3 py-1 rounded-lg bg-black/5">{pagination.totalPages}</span>
            </div>
            <button onClick={() => handlePagination(pagination.currentPage + 1)} disabled={pagination.currentPage === pagination.totalPages}
              className="inline-flex items-center text-sm font-medium transition-colors rounded-sm disabled:opacity-30 disabled:cursor-not-allowed bg-white text-black/80 hover:bg-gray-50 hover:text-black focus:outline-none focus:ring-1 focus:ring-orange-500">
              <ChevronRightIcon className="size-6" />
            </button>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 bg-white rounded-lg shadow-sm">
          <img loading="lazy" src={notresultsfound} alt="No results found" className="w-40 h-auto mb-4" />
        </div>
      )}
    </div>
  );
}

const StatusBox = ({ statusProp, t }) => {
  let bgColor, textColor;
  if (statusProp) {
    switch (statusProp) {
      case 'completed':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'pending':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      case 'failed':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
      default:
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
    }
  }

  return (
    <span className={`capitalize px-2 py-1 text-xs rounded-full ${bgColor} ${textColor}`}>
      {t(statusProp)}
    </span>
  );
}